const site = require('./site.json');
const about = require('../content/aboutPage.json');
const experience = require('../agent/agentExperience').getAgentExperience();
const { registry, isPolicyApproved, getProductLicensing } = require('../licensing/productLicensing');
const { publicDescription, solanaPrices, stripePrice, formatCardPrice, confirmedFree, external } = require('../products/publicProduct');
const { normalizeProductType, resolveProductTypeSectionKey } = require('../products/catalogSections');

const PRIVATE_ROUTES = ['/admin', '/account', '/library', '/checkout', '/private', '/api'];
const HOME_TITLE = 'Audio at Play | Boutique Sound Sample Library';

function normalizePath(pathname) {
  const path = String(pathname || '/').split(/[?#]/)[0];
  return path === '/' ? '/' : path.replace(/\/+$/, '');
}

function canonicalUrl(pathname) {
  return new URL('/' + normalizePath(pathname).replace(/^\/+/, ''), site.origin).href;
}

function cleanDescription(value) {
  return String(value || site.description).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 160);
}

function absoluteImage(value) {
  if (!value) return new URL(site.image, site.origin).href;
  try {
    const url = new URL(value, site.origin);
    if (url.protocol === 'https:' || url.protocol === 'http:') return url.href;
  } catch { /* Use the site image for Storage paths or malformed URLs. */ }
  return new URL(site.image, site.origin).href;
}

function getHomeSchema() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': site.origin + '/#organization',
        name: site.name,
        url: site.origin + '/',
        logo: new URL(site.image, site.origin).href,
      },
      {
        '@type': 'WebSite',
        '@id': site.origin + '/#website',
        name: site.name,
        url: site.origin + '/',
        description: site.description,
        inLanguage: 'en',
        publisher: { '@id': site.origin + '/#organization' },
      },
    ],
  };
}

function getPublicPages() {
  return [
    { path: '/', title: HOME_TITLE, description: site.description },
    { path: '/about', title: about.seo.title, description: about.seo.description },
    ...(registry.policies.some(isPolicyApproved) ? [{ path: '/licensing', title: 'Audio licensing | Audio at Play', description: 'Applicable versioned original-audio license terms.' },
      ...registry.policies.filter(isPolicyApproved).map((p) => ({ path: new URL(p.termsUrl).pathname, title: p.title + ' ' + p.version + ' | Audio at Play', description: p.summary }))] : []),
    { path: '/developers', title: 'Agent Developers | Audio at Play', description: 'Start here for the controlled USDC/SOL agent pilot, access requirements, spending safety and purchase recovery.' },
    {
      path: '/shop',
      title: 'Shop Sounds, Instruments & Samples | Audio at Play',
      description: 'Browse boutique instruments, single sounds, sample packs, Ableton packs, and Max for Live devices at Audio at Play.',
    },
    ...[site.freeSoundsSection, ...site.sections].map((section) => ({
      path: '/browse/' + section.key,
      title: (section.key === 'm4l' ? 'Max for Live Devices' : section.title) + ' | Audio at Play',
      description: section.description + ' Explore the collection at Audio at Play.',
    })),
  ];
}

function getRouteSeo(pathname) {
  const path = normalizePath(pathname);
  const canonicalPath = path === '/single-sounds' ? '/browse/single-sounds' : path;
  const page = getPublicPages().find((entry) => entry.path === canonicalPath) ||
    (path.startsWith('/licensing') ? { title: 'Audio licensing | Audio at Play', description: registry.notice } : null);
  const isPrivate = PRIVATE_ROUTES.some((prefix) => path === prefix || path.startsWith(prefix + '/'));
  const privateTitle = { '/admin': 'Administration', '/account': 'Your Account', '/library': 'Your Library', '/checkout': 'Checkout' }[
    PRIVATE_ROUTES.find((prefix) => path === prefix || path.startsWith(prefix + '/'))
  ];
  const isProduct = /^\/product\/[^/]+$/.test(path);
  return {
    title: page?.title || (isProduct ? 'Sound Product | Audio at Play' : privateTitle ? privateTitle + ' | Audio at Play' : 'Page Not Found | Audio at Play'),
    description: cleanDescription(page?.description),
    canonical: canonicalUrl(canonicalPath),
    image: absoluteImage(),
    noindex: isPrivate || (path.startsWith('/licensing') && !getPublicPages().some((p) => p.path === path)) || (!page && !isProduct),
    schema: path === '/' ? getHomeSchema() : null,
  };
}

function getProductSeo(product, cardPrice = null) {
  return {
    ...getRouteSeo('/product/' + encodeURIComponent(product.slug)),
    title: product.title + ' | Audio at Play',
    description: cleanDescription(publicDescription(product)),
    schema: getProductSchema(product, cardPrice),
    image: absoluteImage(/^https?:\/\//i.test(product.coverImagePath || '') ? product.coverImagePath : ''),
  };
}

function getProductSchema(product, cardPrice = null) {
  if (product.isActive !== true) return null;
  const url = canonicalUrl('/product/' + encodeURIComponent(product.slug));
  const licensing = getProductLicensing(product);
  const license = licensing.url;
  const card = product.listingType === 'native' && !confirmedFree(product) && product.stripeConfig?.enabled ? stripePrice(cardPrice) : null;
  return {
    '@context': 'https://schema.org', '@type': 'Product',
    '@id': url + '#product', name: product.title, description: publicDescription(product), url,
    ...(product.id ? { sku: product.id } : {}),
    ...(license ? { additionalProperty: [
      { '@type': 'PropertyValue', name: 'License terms', value: license },
      ...(licensing.status === 'approved' ? [
        { '@type': 'PropertyValue', name: 'License policy ID', value: licensing.id },
        { '@type': 'PropertyValue', name: 'License version', value: licensing.version },
        { '@type': 'PropertyValue', name: 'Licensor', value: licensing.licensor },
        { '@type': 'PropertyValue', name: 'Attribution required', value: licensing.attributionRequired },
        ...(licensing.attributionRequired && licensing.attributionText ?
          [{ '@type': 'PropertyValue', name: 'Required attribution', value: licensing.attributionText },
            { '@type': 'PropertyValue', name: 'Attribution URL', value: licensing.attributionUrl }] : []),
      ] : []),
    ] } : {}),
    // Crypto is shown as explicit token pricing in visible content. Do not invent an ISO fiat equivalence.
    ...(card ? { offers: { '@type': 'Offer', url, price: card.amount, priceCurrency: card.currency } } : {}),
  };
}

function renderPublicSummary(product = null, cardPrice = null) {
  const links = '<nav aria-label="Agent integration"><a href="/developers">Agent developers: start here</a> | <a href="/agent-solana-usdc-checkout.md">Agent integration guide</a> | ' +
    '<a href="/openapi.json">OpenAPI</a> | <a href="/agent-solana-client.cjs">Node signing client</a></nav>' +
    '<p>Agent purchases require authorized pilot access. Documentation is public.</p>';
  if (!product) return '<div class="page hero glass"><h1>Audio at Play</h1><p class="hero-subtitle">' +
    escapeHtml(site.tagline) + '</p><p>' + escapeHtml(site.collectionDescription) +
    '</p><p><a href="/shop">Explore the sounds</a> | <a href="/about">About Audio at Play</a></p>' + links + '</div>';
  const card = product.stripeConfig?.enabled ? stripePrice(cardPrice) : null;
  const crypto = solanaPrices(product);
  const primaryCrypto = !card ? crypto.find((price) => price.currency === 'USDC') || crypto[0] : null;
  const secondaryCrypto = primaryCrypto ? crypto.filter((price) => price.currency !== primaryCrypto.currency) : crypto;
  const primary = card ? formatCardPrice(cardPrice) + ' - Card' : primaryCrypto ?
    primaryCrypto.amount + ' ' + primaryCrypto.currency + ' (Sol)' : product.stripeConfig?.enabled ?
      'Card pricing unavailable. Confirm the price at checkout.' : 'Pricing unavailable.';
  const prices = confirmedFree(product) ? ['Free - Google sign-in required to download.'] :
    external(product) ? ['Price and purchase on the external store.'] :
      [primary, ...secondaryCrypto.map((p) => p.amount + ' ' + p.currency + ' - Solana'),
        ...(primaryCrypto && product.stripeConfig?.enabled ? ['Card pricing unavailable'] : [])];
  const license = getProductLicensing(product).url;
  return '<article class="page card"><h1>' + escapeHtml(product.title) + '</h1><p class="product-description">' +
    escapeHtml(publicDescription(product)) + '</p>' +
    (prices.length ? prices : ['Pricing unavailable.']).map((p, index) => '<p class="' +
      (index === 0 ? 'product-price-primary' : 'product-price-secondary') + '">' + escapeHtml(p) + '</p>').join('') +
    (license ? '<p><a href="' + escapeHtml(license) + '">Read the applicable license</a></p>' :
      '<p>License terms have not been provided. Permitted use is unconfirmed.</p>') +
    (Array.isArray(product.includedFileTypes) && product.includedFileTypes.length ?
      '<p>Included formats: ' + product.includedFileTypes.filter((v) => typeof v === 'string').map(escapeHtml).join(', ') + '</p>' : '') +
    renderApprovedLicense(product) + renderLicenseProposal(product) + '<p>Enable JavaScript to listen to available previews and continue to purchase or download.</p>' + links + '</article>';
}


function renderApprovedLicense(product) {
  const license = getProductLicensing(product);
  if (license.status !== 'approved') return '';
  const policy = registry.policies.find((entry) => entry.id === license.id && entry.version === license.version);
  return '<section><h2>' + escapeHtml(policy?.title || 'Audio license') + ' (' + escapeHtml(license.version) +
    ')</h2><p>Policy ID: ' + escapeHtml(license.id) + '.</p><p>Licensor: ' + escapeHtml(license.licensor) + '.</p><p>' +
    escapeHtml(license.summary || '') + '</p><p>' + (license.attributionRequired ? 'Attribution required: ' +
      '<code>' + escapeHtml(license.attributionText) + '</code>' : 'Attribution is optional under this policy.') + '</p></section>';
}
function renderPolicyTerms(policy) {
  return '<article id="' + escapeHtml(policy.reviewPath ? policy.kind : policy.id) + '"><h2>' + escapeHtml(policy.title) +
    '</h2><p>' + (isPolicyApproved(policy) ? 'Version' : policy.status === 'draft' ? 'Historical draft version' : 'Release candidate - not active, version') +
    ' ' + escapeHtml(policy.version) + '. Policy ID: ' + escapeHtml(policy.id) + '.</p><p>' +
    (isPolicyApproved(policy) ? 'Licensor: ' + escapeHtml(policy.licensor) : 'This proposal grants no current usage rights.') +
    '</p><h3>Attribution</h3><p>' + escapeHtml(policy.attributionTerms) + '</p>' +
    (policy.sections || registry.commonSections).map((s) => '<h3>' + escapeHtml(s.title) + '</h3><p>' + escapeHtml(s.text) + '</p>').join('') + '</article>';
}
function renderLicenseProposal(product) {
  const licensing = getProductLicensing(product);
  if (!licensing.proposal) return '';
  const proposal = licensing.proposal;
  return '<aside><h2>Proposed terms - awaiting approval</h2><p>' +
    escapeHtml(proposal.attributionRequired ? 'Free sounds would require crediting the author and linking to https://audioatplay.ca.' :
      'Attribution would be optional for paid sounds.') + '</p>' +
    (proposal.attributionRequired ? proposal.attributionText ?
      '<p>Proposed credit: <a href="' + escapeHtml(proposal.attributionUrl) + '">' +
        escapeHtml(proposal.attributionText) + '</a></p>' :
      '<p>Author credit has not been provided. Confirm the author&#39;s public credit before using this sound.</p>' : '') +
    '<p><a href="' +
    escapeHtml(proposal.reviewUrl) + '">Review draft version ' + escapeHtml(proposal.version) +
    '</a></p><p>This proposal grants no current usage rights and does not change earlier purchases.</p></aside>';
}
function renderCatalogPage(pathname, products) {
  const sections = [site.freeSoundsSection, ...site.sections];
  const section = sections.find((entry) => pathname === '/browse/' + entry.key);
  const selected = products.filter((product) => product.isActive === true &&
    (pathname === '/shop' || (section.key === 'free-sounds' ? confirmedFree(product) :
      resolveProductTypeSectionKey(normalizeProductType(product.productType)) === section.key)));
  const title = section ? section.title : 'Find Your Next Sound';
  const description = section ? section.description : 'Search sounds, listen to previews, and compare free and paid releases.';
  return '<main class="page grid"><header class="card"><h1 class="section-heading">' + escapeHtml(title) +
    '</h1><p>' + escapeHtml(description) + '</p></header><nav aria-label="Sound collections"><a href="/shop">All sounds</a> | ' +
    sections.map((entry) => '<a href="/browse/' + entry.key + '">' + escapeHtml(entry.title) + '</a>').join(' | ') +
    '</nav><div class="grid products home-product-cards">' + selected.map((product) =>
      '<article class="card"><h2><a href="/product/' + encodeURIComponent(product.slug) + '">' + escapeHtml(product.title) +
      '</a></h2><p>' + escapeHtml(publicDescription(product)) + '</p></article>').join('') +
    '</div>' + (!selected.length ? '<p>No products are available in this collection right now. <a href="/shop">Explore all sounds</a>.</p>' : '') +
    '</main>';
}

function renderInformationalPage(pathname, products = []) {
  if (pathname === '/shop' || [site.freeSoundsSection, ...site.sections].some((section) => pathname === '/browse/' + section.key)) {
    return renderCatalogPage(pathname, products);
  }
  if (pathname === '/404') {
    return '<main class="page"><div class="card"><h1 class="section-heading">Page Not Found</h1>' +
      '<p>The page you requested does not exist.</p><p><a href="/shop">Explore the sounds</a> | ' +
      '<a href="/">Go Home</a></p></div></main>';
  }
  if (pathname === '/about') {
    const link = (entry, className = 'text-link') => '<a class="' + className + '" href="' +
      escapeHtml(entry.href) + '">' + escapeHtml(entry.label) + '</a>';
    const section = (id, title, content, className = '') => '<section class="card grid ' + className +
      '" aria-labelledby="' + id + '"><h2 class="section-heading" id="' + id + '">' +
      escapeHtml(title) + '</h2>' + content + '</section>';
    return '<div class="app-shell"><main><article class="page grid about-page" aria-labelledby="about-title">' +
      '<header class="hero glass about-hero"><p class="eyebrow">' + escapeHtml(about.eyebrow) +
      '</p><h1 id="about-title">' + about.heading.split('. ').map((phrase, index, phrases) =>
        '<span class="about-title-line">' + escapeHtml(phrase + (index < phrases.length - 1 ? '. ' : '')) +
        '</span>').join('') + '</h1><p class="hero-subtitle">' + escapeHtml(site.tagline) +
      '</p><p>' + escapeHtml(about.intro) +
      '</p></header><div class="about-story">' +
      section('about-making-title', about.making.heading, about.making.paragraphs.map((p) => '<p>' +
        escapeHtml(p) + '</p>').join(''), 'about-making') +
      section('about-people-title', about.people.heading, '<p>' + escapeHtml(about.people.human) +
        '</p><p>' + escapeHtml(about.people.agentIntro) + ' ' + link(about.people.documentation) + ' ' +
        escapeHtml(about.people.agentOutro) + '</p><p>' + escapeHtml(about.people.rights) + '</p>') +
      section('about-maker-title', about.maker.heading, '<p>' + escapeHtml(about.maker.bio) +
        '</p><ul class="about-project-links">' + about.maker.projects.map((project) => '<li>' +
          link(project) + '</li>').join('') + '</ul>') + '</div>' +
      section('about-invitation-title', about.invitation.heading, '<p>' + escapeHtml(about.invitation.text) +
        '</p><div class="btn-row">' + link(about.invitation.shop, 'btn btn-primary') + ' ' +
        link(about.invitation.developers, 'btn btn-secondary') + '</div><p class="about-license-link">' +
        link(about.invitation.licensing) + '</p>', 'about-invitation') + '</article></main></div>';
  }
  if (pathname === '/developers') {
    return '<main class="page"><h1>' + escapeHtml(experience.title) + '</h1><p>' + escapeHtml(experience.intro) +
      '</p><nav aria-label="Developer resources">' + experience.resources.map((r) =>
        '<p><a href="' + escapeHtml(r.url) + '">' + escapeHtml(r.label) + '</a></p>').join('') + '</nav>' +
      experience.sections.map((s) => '<section id="' + escapeHtml(s.id) + '"><h2>' + escapeHtml(s.title) + '</h2>' +
        s.paragraphs.map((p) => '<p>' + escapeHtml(p) + '</p>').join('') +
        (s.id === 'request-access' && experience.requestUrl ? '<a href="' + escapeHtml(experience.requestUrl) + '">Request agent access</a>' : '') +
        (s.id === 'support' && experience.supportUrl ? '<a href="' + escapeHtml(experience.supportUrl) + '">Contact support</a>' : '') + '</section>').join('') +
      '<h2>Recognize the outcome before retrying</h2>' + experience.recovery.map((r) =>
        '<section><h3>' + escapeHtml(r.outcome) + '</h3><p>' + escapeHtml(r.action) + '</p></section>').join('') +
      '<h2>Start with a read-only inspection</h2><pre><code>' + escapeHtml(experience.example) + '</code></pre></main>';
  }
  if (pathname === '/licensing' || pathname.startsWith('/licensing/')) {
    const policies = pathname === '/licensing' ? registry.policies.filter((p) => p.sections) : registry.policies.filter((p) => p.termsUrl === registry.origin + pathname);
    return '<main class="page"><h1>Audio licensing</h1><p>Only the applicable policy explicitly assigned to a product covers that recording. Earlier acquisitions retain their recorded terms.</p>' +
      (registry.policies.some(isPolicyApproved) ? '' : '<p>' + escapeHtml(registry.notice) + '</p>') +
      (policies.length ? policies.map(renderPolicyTerms).join('') : '<p>License version not found.</p>') +
      (pathname === '/licensing' ? '<details><summary>Historical policy archive</summary><p>These earlier drafts are retained for reference. They are not the 1.0 licenses above.</p>' + registry.policies.filter((p) => p.reviewPath).map(renderPolicyTerms).join('') + '</details>' : '') + '</main>';
  }
  return renderPublicSummary();
}

function metaEntries(metadata) {
  const entries = [
    ['name', 'description', metadata.description],
    ['name', 'robots', metadata.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'],
    ['property', 'og:type', 'website'],
    ['property', 'og:site_name', site.name],
    ['property', 'og:locale', 'en_CA'],
    ['property', 'og:title', metadata.title],
    ['property', 'og:description', metadata.description],
    ['property', 'og:url', metadata.canonical],
    ['property', 'og:image', metadata.image],
    ['property', 'og:image:alt', metadata.title],
    ['name', 'twitter:card', 'summary'],
    ['name', 'twitter:title', metadata.title],
    ['name', 'twitter:description', metadata.description],
    ['name', 'twitter:image', metadata.image],
    ['name', 'twitter:image:alt', metadata.title],
  ];
  return entries.filter((entry) => entry[2]);
}

function escapeHtml(value) {
  return String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[character]));
}

function serializeSchema(schema) {
  return JSON.stringify(schema).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026');
}

function renderSeoHead(metadata) {
  return [
    '<title>' + escapeHtml(metadata.title) + '</title>',
    ...metaEntries(metadata).map(([attribute, key, content]) =>
      '<meta data-seo="' + key + '" ' + attribute + '="' + key + '" content="' + escapeHtml(content) + '" />'
    ),
    ...(metadata.canonical ? ['<link data-seo="canonical" rel="canonical" href="' + escapeHtml(metadata.canonical) + '" />'] : []),
    ...(metadata.schema ? ['<script id="site-schema" type="application/ld+json">' + serializeSchema(metadata.schema) + '</script>'] : []),
  ].join('\n    ');
}

function renderRobots() {
  // Crawlers must see the existing noindex headers/meta tags on private pages.
  // Firebase authentication and security rules protect data, not robots.txt.
  return [
    'User-agent: *',
    'Allow: /',
    '',
    'Sitemap: ' + site.origin + '/sitemap.xml',
    '',
  ].join('\n');
}

function renderSitemap(products) {
  const entries = new Map(getPublicPages().map((page) => [canonicalUrl(page.path), null]));
  for (const product of products) {
    if (product.isActive === false) continue;
    if (!/^[a-z0-9][a-z0-9._-]*$/i.test(product.slug || '')) {
      throw new Error('An active product has an invalid URL slug. Use letters, numbers, dots, underscores, or hyphens.');
    }
    const url = canonicalUrl('/product/' + encodeURIComponent(product.slug));
    const date = new Date(product.updatedAt || '');
    entries.set(url, Number.isNaN(date.getTime()) ? null : date.toISOString());
  }
  if (entries.size > 50000) throw new Error('The sitemap exceeds 50,000 URLs; split it into a sitemap index.');
  return '<?xml version="1.0" encoding="UTF-8"?>\n' +
    '<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n' +
    [...entries].map(([url, lastmod]) =>
      '  <url><loc>' + escapeHtml(url) + '</loc>' + (lastmod ? '<lastmod>' + lastmod + '</lastmod>' : '') + '</url>'
    ).join('\n') + '\n</urlset>\n';
}

module.exports = {
  site, PRIVATE_ROUTES, normalizePath, canonicalUrl, cleanDescription, absoluteImage,
  getHomeSchema, getPublicPages, getRouteSeo, getProductSeo, metaEntries,
  serializeSchema, renderSeoHead, renderRobots, renderSitemap, getProductSchema, renderPublicSummary, renderInformationalPage,
};
